import DOMPurify from "dompurify";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/client";
import React, { useMemo, useCallback } from "react";
import { Flex, StatusDot, Popover, Small, Spacer } from "orcs-design-system";
import { withRouter } from "react-router";
import { get, isEmpty } from "lodash";
import { getPrincipleStatus } from "src/pages/TeamDetailPage/team.graphql";
import { teamDetailsPath } from "../../util/routing";

const BADGE_DETAILS_MAP = {
  TRUE: {
    colour: "success",
    text: "MET",
  },
  FALSE: {
    colour: "danger",
    text: "NOT MET",
  },
  INSUFFICIENT_DATA: {
    colour: "warning",
    text: "N/A",
  },
};

const PRINCIPLE_VARIANT_MAP = {
  TRUE: "success",
  FALSE: "danger",
  INSUFFICIENT_DATA: "warning",
};

const PRINCIPLE_TEST_ID_MAP = {
  TRUE: "success-dot",
  FALSE: "failure-dot",
  INSUFFICIENT_DATA: "warning-dot",
};

const createTitleMarkup = (principle) => ({
  __html: DOMPurify.sanitize(principle.title),
});

const PrincipleStatusText = ({ principle }) => (
  <>
    <div
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={createTitleMarkup(principle)}
    />
    <Small fontWeight="bold">
      STATUS :{" "}
      {principle.signal
        ? principle.signal.text
        : BADGE_DETAILS_MAP[principle.met]?.text ||
          BADGE_DETAILS_MAP.INSUFFICIENT_DATA.text}
    </Small>
  </>
);

PrincipleStatusText.propTypes = {
  principle: PropTypes.object.isRequired,
};

const PrincipleStatusQuery = ({ team, history, textDirection, marginTop }) => {
  const { data } = useQuery(getPrincipleStatus, {
    variables: { teamId: team?.id },
    skip: !team?.id,
  });

  const principles = get(data, "team.principles", []);

  const handlePrincipleClick = useCallback(() => {
    if (team?.id) {
      history.push(teamDetailsPath(team, "principles"));
    }
  }, [team, history]);

  const statusDots = useMemo(() => {
    if (isEmpty(principles) || !team?.id) {
      return [];
    }

    return principles.map((principle) => {
      const variant = principle.signal
        ? principle.signal.color
        : PRINCIPLE_VARIANT_MAP[principle.met] ||
          PRINCIPLE_VARIANT_MAP.INSUFFICIENT_DATA;

      const testId = principle.signal
        ? `${principle.signal.color}-dot`
        : PRINCIPLE_TEST_ID_MAP[principle.met] ||
          PRINCIPLE_TEST_ID_MAP.INSUFFICIENT_DATA;

      const key = `${team.id}-${principle.title}`;

      return (
        <Popover
          key={key}
          direction={textDirection || "bottomRight"}
          text={<PrincipleStatusText principle={principle} />}
          textAlign="left"
          width="300px"
        >
          <StatusDot
            data-testId={testId}
            variant={variant}
            onClick={handlePrincipleClick}
          />
        </Popover>
      );
    });
  }, [principles, team, textDirection, handlePrincipleClick]);

  if (statusDots.length === 0) {
    return null;
  }

  return (
    <Flex alignItems="center" mt={marginTop}>
      <Spacer mx="xxs">{statusDots}</Spacer>
    </Flex>
  );
};

// TODO: Fix team def, object
PrincipleStatusQuery.propTypes = {
  team: PropTypes.object,
  history: PropTypes.object,
  textDirection: PropTypes.string,
  marginTop: PropTypes.string,
};

export default withRouter(PrincipleStatusQuery);
